import type { CreateOperationsLogEntryRequest, OperationsLogType } from "./api";

/** The three entry types of [F-015], in the order the SRS names them. */
export const OPERATIONS_LOG_TYPES: readonly OperationsLogType[] = [
  "systemaenderung",
  "wartung",
  "besonderes_ereignis",
];

// Resolved through a variable (t(labelKey)), so the i18n gate checks the
// values below rather than a partial key; every one exists in de.ts.
export const OPERATIONS_LOG_TYPE_LABEL_KEY: Record<OperationsLogType, string> = {
  systemaenderung: "operationsLog.type.systemaenderung",
  wartung: "operationsLog.type.wartung",
  besonderes_ereignis: "operationsLog.type.besonderes_ereignis",
};

/** The create form as the dialog holds it: `datetime-local` strings, local time. */
export interface EntryDraft {
  type: OperationsLogType | "";
  startAt: string;
  endAt: string;
  areaIds: string[];
  shortDescription: string;
}

export type EntryDraftField = "type" | "startAt" | "endAt" | "areaIds" | "shortDescription";

/** i18n keys of the field errors, by field. */
export type EntryDraftErrors = Partial<Record<EntryDraftField, string>>;

export const EMPTY_DRAFT: EntryDraft = {
  type: "",
  startAt: "",
  endAt: "",
  areaIds: [],
  shortDescription: "",
};

/** Local `datetime-local` value (YYYY-MM-DDTHH:mm) of an instant. */
export function toLocalDateTimeValue(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function instant(local: string): Date | null {
  if (!local) return null;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * The request for a draft, or the field errors that stop it ([F-015]
 * Pflichtfelder: Typ, Zeitpunkt, Bereich, Kurzbeschreibung; the end is
 * optional and not before the start). The server checks all of it again.
 */
export function buildCreateRequest(
  draft: EntryDraft,
): { request: CreateOperationsLogEntryRequest; errors?: undefined } | { request?: undefined; errors: EntryDraftErrors } {
  const errors: EntryDraftErrors = {};
  const start = instant(draft.startAt);
  const end = instant(draft.endAt);
  const description = draft.shortDescription.trim();
  if (!draft.type) errors.type = "operationsLog.validation.typeRequired";
  if (!start) errors.startAt = "operationsLog.validation.startRequired";
  if (draft.endAt && !end) errors.endAt = "operationsLog.validation.endInvalid";
  if (start && end && end.getTime() < start.getTime()) errors.endAt = "operationsLog.validation.endBeforeStart";
  if (draft.areaIds.length === 0) errors.areaIds = "operationsLog.validation.areasRequired";
  if (!description) errors.shortDescription = "operationsLog.validation.descriptionRequired";
  if (Object.keys(errors).length > 0 || !start || !draft.type) return { errors };
  return {
    request: {
      type: draft.type,
      startAt: start.toISOString(),
      ...(end ? { endAt: end.toISOString() } : {}),
      areaIds: draft.areaIds,
      shortDescription: description,
    },
  };
}
